import { View } from 'react-native';
import { fasShieldKeyhole } from 'shared/@generated/icons/fasShieldKeyhole';
import { Icon } from 'shared/components/icon';
import { Text } from 'shared/components/text';
import { illustrationPalette as palette } from './illustration-palette';
import type { IllustrationScale } from './illustration-scene';
import {
  Caption,
  IllustrationFrame,
  Phone,
  useIllustrationCopy,
  type IllustrationProps,
} from './illustration-parts';

type Scaled = { s: IllustrationScale };
type Sized = Scaled & { width: number };

function PaymentRadar({ s }: Scaled) {
  return (
    <View testID="payment-radar" pointerEvents="none" className="absolute inset-0">
      {[148, 112, 76].map((diameter, i) => (
        <View
          key={diameter}
          testID="payment-radar-ring"
          className="absolute rounded-full"
          style={{
            left: s(80 - diameter / 2),
            top: s(80 - diameter / 2),
            width: s(diameter),
            height: s(diameter),
            borderWidth: s(0.9),
            borderColor: palette.forest,
            opacity: [0.12, 0.18, 0.24][i],
          }}
        />
      ))}
    </View>
  );
}

function PetPayMark({ s, width = 54 }: Sized) {
  return (
    <View
      className="flex-row items-center justify-center"
      style={{
        width: s(width),
        height: s(18),
        gap: s(4),
        backgroundColor: palette.ink,
        borderRadius: s(9),
      }}
    >
      <Icon
        icon={fasShieldKeyhole}
        className="text-brand-cream-light"
        style={{ width: s(8), height: s(9) }}
      />
      <Text
        className="text-brand-cream-light font-semibold"
        style={{ fontSize: s(7.5), lineHeight: s(11) }}
      >
        PetPay
      </Text>
    </View>
  );
}

export function Payment({ transparent = false }: IllustrationProps) {
  const t = useIllustrationCopy();
  return (
    <IllustrationFrame transparent={transparent}>
      {(s) => {
        const phoneWidth = 76;
        const upperPhoneTop = -103;
        const lowerPhoneTop = 160 - (upperPhoneTop + (phoneWidth * 124) / 58);
        return (
          <>
            <PaymentRadar s={s} />
            <Phone s={s} x={42} y={upperPhoneTop} width={phoneWidth} />
            <Phone s={s} x={42} y={lowerPhoneTop} width={phoneWidth} />
            <View
              testID="payment-upper-copy"
              className="absolute"
              style={{ left: s(47.6), top: s(6), width: s(64.8), gap: s(1.5) }}
            >
              <Caption s={s} size={6} muted center>
                {t('buyer')}
              </Caption>
              <Caption s={s} size={8.5} center>
                {t('buyer_name')}
              </Caption>
              <Caption s={s} size={8.5} green center>
                {t('price')}
              </Caption>
            </View>
            <View
              testID="payment-lower-copy"
              className="absolute"
              style={{ left: s(47.6), bottom: s(6), width: s(64.8), gap: s(1.5) }}
            >
              <Caption s={s} size={8.5} green center>
                {t('price')}
              </Caption>
              <Caption s={s} size={8.5} center>
                {t('seller')}
              </Caption>
              <Caption s={s} size={6} muted center>
                {t('seller_label')}
              </Caption>
            </View>
            {[-16, -12, 12, 16].map((offset) => (
              <View
                key={offset}
                testID="payment-dot"
                className="absolute rounded-full"
                style={{
                  left: s(80 - 1.2),
                  top: s(80 + offset - 1.2),
                  width: s(2.4),
                  height: s(2.4),
                  backgroundColor: palette.ink,
                }}
              />
            ))}
            <View
              testID="payment-transfer-mark"
              className="absolute"
              style={{ left: s(53), top: s(71) }}
            >
              <PetPayMark s={s} width={54} />
            </View>
          </>
        );
      }}
    </IllustrationFrame>
  );
}
